import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Grid from '@mui/material/Grid';
import BrokerDatePicker from '../../../../common/BrokerDatePicker';
import OutlinedSelect from '../../../../common/OutlinedSelect';
import {
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  TextField,
  Tooltip,
} from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import makeStyles from '@mui/styles/makeStyles';
import SearchIcon from '@mui/icons-material/Search';
import PropTypes from 'prop-types';

import CustomPanel from '../../../../common/CustomPanel';
import * as Constants from '../../../../../utils/Constants';
import { PICKER_FORMAT_DATE } from '../../../../../utils/Constants';
import { findByIdentification } from '../actions/find_by_identification';
import useFindByIdentification from '../hooks/useFindByIdentification';
import { isValidId } from '../../../../../utils/id-validator';
import {
  selectDivisionPolitica,
  selectGender,
  selectRelationship,
  selectTypeIdentification,
} from '../utils/selectors';
import { addSweetAlert, closeSweetAlert } from '../../../../common/SweetAlert/actions/sweet_alert';
import { resetFormApplicationValues } from '../../../../../utils/CommonUtils';
import { ResetApplicationForm } from '../../Payment/Enums';

const useStyles = makeStyles((theme) => ({
  formControl: {
    margin: theme.spacing(1),
  },
  radioGroup: {
    marginTop: 10,
  },
}));

const setInsuranceHolderForm = (holder, setValue) => {
  const { Person: person } = holder;
  setValue('insuranceHolder.relationshipBeneficiary', holder.RelationshipBeneficiary.trim() || '');
  setValue('insuranceHolder.identificationType', person?.IdentificationType.trim() || '');
  setValue('insuranceHolder.identification', person?.Identification.trim() || '');
  setValue('insuranceHolder.name', person?.Name.trim() || '');
  setValue('insuranceHolder.lastName', person?.LastName.trim() || '');
  setValue('insuranceHolder.birthDate', person?.BirthDate.trim() || '');
  setValue('insuranceHolder.birthPlace', person?.BirthPlace.trim() || '');
  setValue('insuranceHolder.gender', person?.Gender.trim() || '');
};

const InfinityHolder = ({ editionMode }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const relationship = useSelector(selectRelationship);
  const typeIdentification = useSelector(selectTypeIdentification);
  const gens = useSelector(selectGender);
  const { requestData: quoteData } = useSelector((state) => state.quote);
  const { register, control, formState: { errors }, getValues, setValue, clearErrors, watch } = useFormContext();
  const countries = useSelector(selectDivisionPolitica);

  const getApplication = useSelector((state) => state.getApplication);

  const [isDisableInput, setIsDisableInput] = React.useState(true);
  const [personWasFound, setPersonWasFound] = React.useState(true);

  const identificationWatch = watch('insuranceHolder.identification');


  useFindByIdentification(clearErrors, setValue, setIsDisableInput, setPersonWasFound);

  const handleSearchIdentification = () => {
    const formValues = getValues();
    const identification = formValues.insuranceHolder?.identification;
    if (identification) {
      if (isValidId(identification)) {
        dispatch(findByIdentification(identification, quoteData?.productId));
      } else {
        dispatch(addSweetAlert({
          type: 'warning',
          title: Constants.MESSAGE_INVALID_IDENTIFICATION,
          showCancel: false,
          onConfirmLabel: 'Aceptar',
          onConfirm: () => {
            dispatch(closeSweetAlert());
          },
        }));
      }
    }
  };

  useEffect(() => {
    register('insuranceHolder.identification');
  }, [register]);

  useEffect(() => {
    if (editionMode) {
      const applicationHolder = getApplication.response.InsuranceApplicationHolder;
      setIsDisableInput(applicationHolder.Person.IsValidatedCivilRegistry);
      setInsuranceHolderForm(applicationHolder, setValue);
    }
  }, [editionMode, getApplication.response.InsuranceApplicationHolder]);

  const onChangeIdentification = (e) => {
    resetFormApplicationValues(setValue, clearErrors, ResetApplicationForm.HolderFields);
    setValue('insuranceHolder.identification', e.target?.value);
  };

  const relacionRef = useRef();
  const tipoIdRef = useRef();
  const nombresRef = useRef();
  const apellidosRef = useRef();
  const sexoRef = useRef();
  const lugarRef = useRef();

  return (
    <CustomPanel withoutTitle subtitle="Datos Titular">
      <Grid container direction="row" spacing={3}>
        <Grid item md={4} xs={12}>
          <FormControl
            className={classes.formControl}
            fullWidth
            error={errors.insuranceHolder?.hasOwnProperty('relationshipBeneficiary')}
          >
            <InputLabel id="id-relationshipBeneficiary" style={{ fontSize: 11 }}>
              RELACIÓN CON BENEFICIARIO
            </InputLabel>
            <Controller
              name="insuranceHolder.relationshipBeneficiary"
              control={control}
              defaultValue=""
              render={({ field: { ref, ...fieldProps } }) => (
                <OutlinedSelect {...fieldProps} autoFocus inputRef={ref} labelId="id-relationshipBeneficiary" label="RELACIÓN CON BENEFICIARIO">
                  <MenuItem value="">
                    <em>Seleccione..</em>
                  </MenuItem>
                  {
                    relationship?.map((relation) => (
                      <MenuItem
                        key={relation.id}
                        value={relation.id}
                      >
                        {relation.description}
                      </MenuItem>
                    ))
                  }
                </OutlinedSelect>
              )}
            />
            <FormHelperText>
              {errors.insuranceHolder?.relationshipBeneficiary?.message}
            </FormHelperText>
          </FormControl>
        </Grid>
        <Grid item md={4} xs={12}>
          <FormControl
            className={classes.formControl}
            fullWidth
            error={errors.insuranceHolder?.hasOwnProperty('identificationType')}
          >
            <InputLabel id="id-type-identification">TIPO DE IDENTIFICACIÓN</InputLabel>
            <Controller
              name="insuranceHolder.identificationType"
              control={control}
              defaultValue={Constants.DEFAULT_VALUE_TYPE_IDENTIFICATION}
              render={({ field: { ref, ...fieldProps } }) => (
                <OutlinedSelect {...fieldProps} inputRef={ref} labelId="id-type-identification" label="TIPO DE IDENTIFICACIÓN">
                  <MenuItem value="">
                    <em>Seleccione...</em>
                  </MenuItem>
                  {
                    typeIdentification?.map((t) => (
                      <MenuItem
                        key={t.id}
                        value={t.id}
                      >
                        {t.description}
                      </MenuItem>
                    ))
                  }
                </OutlinedSelect>
              )}
            />
            <FormHelperText>
              {errors.insuranceHolder?.identificationType?.message}
            </FormHelperText>
          </FormControl>
        </Grid>
        <Grid item md={4} xs={12}>
          <TextField
            fullWidth
            type="number"
            className={classes.formControl}
            value={identificationWatch}
            onChange={onChangeIdentification}
            label="IDENTIFICACIÓN"
            autoComplete="off"
            error={errors.insuranceHolder?.hasOwnProperty('identification')}
            helperText={errors.insuranceHolder?.identification?.message}
            InputProps={{
              endAdornment: (
                <Tooltip title="Buscar" arrow>
                  <IconButton
                    aria-label="search"
                    size="small"
                    onClick={handleSearchIdentification}
                  >
                    <SearchIcon />
                  </IconButton>
                </Tooltip>
              ),
            }}
          />
        </Grid>
      </Grid>
      <Grid container direction="row" spacing={3}>
        <Grid item md={4} xs={12}>
          <Controller
            name="insuranceHolder.name"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                {...field}
                inputRef={nombresRef}
                onFocus={() => nombresRef.current.focus()}
                fullWidth
                className={classes.formControl}
                label="NOMBRES"
                autoComplete="off"
                InputProps={{ readOnly: isDisableInput }}
                error={errors.insuranceHolder?.hasOwnProperty('name')}
                helperText={errors.insuranceHolder?.name?.message}
              />
            )}
          />
        </Grid>
        <Grid item md={4} xs={12}>
          <Controller
            name="insuranceHolder.lastName"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                {...field}
                inputRef={apellidosRef}
                onFocus={() => apellidosRef.current.focus()}
                fullWidth
                className={classes.formControl}
                label="APELLIDOS"
                autoComplete="off"
                InputProps={{ readOnly: isDisableInput }}
                error={errors.insuranceHolder?.hasOwnProperty('lastName')}
                helperText={errors.insuranceHolder?.name?.message}
              />
            )}
          />
        </Grid>
        <Grid item md={4} xs={12}>
          <Controller
            name="insuranceHolder.birthDate"
            control={control}
            defaultValue={null}
            render={({ field }) => (
              <BrokerDatePicker
                {...field}
                readOnly={isDisableInput}
                disableFuture
                className={classes.formControl}
                fullWidth
                format={PICKER_FORMAT_DATE}
                margin="normal"
                label="FECHA NACIMIENTO"
                InputProps={{ readOnly: isDisableInput }}
                error={errors.insuranceHolder?.hasOwnProperty('birthDate')}
                helperText={errors.insuranceHolder?.birthDate?.message}
              />
            )}
          />
        </Grid>
      </Grid>
      <Grid container item direction="row" spacing={3}>
        <Grid item md={4} xs={12}>
          <FormControl
            className={classes.formControl}
            fullWidth
            error={errors.insuranceHolder?.hasOwnProperty('gender')}
          >
            <InputLabel id="id-gender">SEXO</InputLabel>
            <Controller
              name="insuranceHolder.gender"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <OutlinedSelect {...field} inputRef={sexoRef} onFocus={() => sexoRef.current.focus()} labelId="id-gender" fullWidth label="SEXO" inputProps={{ readOnly: isDisableInput }} error={errors.insuranceHolder?.hasOwnProperty('gender')}>
                  {
                    gens?.map((gen) => (
                      <MenuItem
                        key={gen.id}
                        value={gen.id}
                      >
                        {gen.description}
                      </MenuItem>
                    ))
                  }
                </OutlinedSelect>
              )}
            />
            <FormHelperText>
              {errors.insuranceHolder?.gender?.message}
            </FormHelperText>
          </FormControl>
        </Grid>
        <Grid item md={4} xs={12}>
          {
            personWasFound
              ? (
                <Controller
                  name="insuranceHolder.birthPlace"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <TextField
                      {...field}
                      inputRef={lugarRef}
                      onFocus={() => lugarRef.current.focus()}
                      fullWidth
                      className={classes.formControl}
                      autoComplete="off"
                      label="LUGAR NACIMIENTO"
                      error={errors.insuranceHolder?.hasOwnProperty('birthPlace')}
                      helperText={errors.insuranceHolder?.birthPlace?.message}
                    />
                  )}
                />
              )
              : (
                <FormControl
                  className={classes.formControl}
                  fullWidth
                  error={errors.insuranceHolder?.birthPlace && true}
                >
                  <InputLabel id="province-select">LUGAR NACIMIENTO</InputLabel>
                  <Controller
                    name="insuranceHolder.birthPlace"
                    control={control}
                    defaultValue="Ecuador"
                    render={({ field: { ref, ...fieldProps } }) => (
                      <OutlinedSelect {...fieldProps} inputRef={ref} labelId="province-select" label="LUGAR NACIMIENTO">
                        <MenuItem value="">
                          <em>Seleccione...</em>
                        </MenuItem>
                        {countries?.map((b) => (
                          b.id !== '0' && (
                            <MenuItem key={b.id} value={b.description}>
                              {b.description}
                            </MenuItem>
                          )
                        ))}
                      </OutlinedSelect>
                    )}
                  />
                  <FormHelperText>
                    {errors.insuranceHolder?.birthPlace.message}
                  </FormHelperText>
                </FormControl>
              )
          }
        </Grid>
      </Grid>
    </CustomPanel>
  );
};

InfinityHolder.propTypes = {
  editionMode: PropTypes.bool.isRequired,
};

export default InfinityHolder;
